import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import getConfig from 'next/config';
import { debounce } from 'lodash';

import {
  AutoCompleteAddress,
  AutoCompleteAddresses,
  DataSuggestions,
} from '@/core/interfaces/common';
import { InputWrapper } from '@/core/components/InputWrapper';

import { fieldDescribedBy } from '@/utils/fieldIds';
import { request } from '@/utils/request';
import {
  findLongestCommonSubstring,
  getFormattedAddress,
} from '@/utils/helpers';

import {
  AddressInputWrapper,
  AddressSuggestion,
  AddressSuggestions,
} from './AutoAddressInput.styles';

interface FormInputProps {
  backgroundcolor: string;
  error: () => string | false;
  /**
   * Optional description of why the field exists or how it is used.
   * Always exposed to screen readers via aria-describedby; visually
   * revealed on demand through a small help toggle inside the field.
   */
  helpText?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  onBlur?: (streetAddress?: string, zipCode?: string) => void;
  onChangeAddress: (event: string) => void;
  onFocus: () => void;
  /**
   * Optional callback invoked when the browser fires the `invalid` event on the input.
   * Called after the native tooltip is suppressed via `event.preventDefault()`.
   * Use this to set a styled inline error message in place of the browser's native tooltip.
   */
  onInvalidField?: () => void;
  setZipCode: (event: string) => void;
  value: string;
  ariaLabel?: string;
}

const { REACT_APP_SMARTY_STREETS_API_KEY } = getConfig().publicRuntimeConfig;

export const AutoAddressInput = ({
  backgroundcolor,
  error,
  helpText,
  isDisabled,
  isRequired,
  onBlur,
  onChangeAddress,
  onFocus,
  onInvalidField,
  setZipCode,
  value,
  ariaLabel,
}: FormInputProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const describedBy = fieldDescribedBy({
    fieldName: 'streetAddress',
    hasError: !!error(),
    hasHelp: !!helpText,
  });

  const [
    addressSuggestions,
    setAddressSuggestions,
  ] = useState<AutoCompleteAddresses>([]);
  const [
    isAutocompletePending,
    setIsAutocompletePending,
  ] = useState(false);
  const [
    showSuggestions,
    setShowSuggestions,
  ] = useState(false);
  const [
    focusedIndex,
    setFocusedIndex,
  ] = useState(-1);

  const handleFetchAddressSuggestions = async (
    inputValue: string, params: { selected?: string }
  ) => {
    setIsAutocompletePending(true);

    const addressSuggestion = !inputValue ?
      [] :
      await request
        .get<DataSuggestions>(
          `https://us-autocomplete-pro.api.smartystreets.com/lookup?key=${REACT_APP_SMARTY_STREETS_API_KEY}`,
          {
            params: {
              search: inputValue,
              ...params,
            },
          }
        )
        .then(({ data }) => data?.suggestions)
        .catch(({ response: { data = {} } = {} }) => {
          throw data;
        });

    setAddressSuggestions(addressSuggestion || []);
    setIsAutocompletePending(false);
    setShowSuggestions(true);
  };

  const debouncedFetchAddressSuggestions = useCallback(
    debounce((newInputValue: string) => {
      handleFetchAddressSuggestions(newInputValue, {});
    }, 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handlePickAddressSuggestion = (suggestion: AutoCompleteAddress) => {
    if (suggestion.entries > 1) {
      const selected = `${suggestion.streetLine} ${suggestion.secondary} (${suggestion.entries}) ${suggestion.city} ${suggestion.state} ${suggestion.zipcode}`;

      handleFetchAddressSuggestions(value, { selected });
    } else {
      setShowSuggestions(false);
    }

    setZipCode(suggestion.zipcode);
    onChangeAddress(`${suggestion.streetLine} ${suggestion.secondary}`);

    if (onBlur) {
      onBlur(`${suggestion.streetLine} ${suggestion.secondary}`, suggestion.zipcode);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target as Element)) {
      setShowSuggestions(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  });

  // Sync input value with prop value
  useEffect(() => {
    const input = inputRef.current;

    if (input && input.value !== value) {
      input.value = value;
    }
  }, [value]);

  // Detect browser autofill - browser autofill does NOT otherwise
  // trigger the target lookup. This is required for message mapped
  // action centers.
  useEffect(() => {
    const input = inputRef.current;

    if (!input) return undefined;

    const checkForAutofill = (): void => {
      const currentValue = input.value;

      // Check if zip code field also got autofilled
      const zipCodeInput = document.querySelector('input[name="zipCode"]') as HTMLInputElement;
      const autofilledZipCode = zipCodeInput?.value || '';

      if (currentValue && currentValue !== value) {
        onChangeAddress(currentValue);

        // If zip code was autofilled, update it via setZipCode
        if (autofilledZipCode) {
          setZipCode(autofilledZipCode);
        }

        if (onBlur) {
          onBlur(currentValue, autofilledZipCode);
        }
      }
    };

    // Animation event for webkit autofill detection
    const handleAnimationStart = (e: AnimationEvent): void => {
      if (e.animationName === 'onAutoFillStart') {
        checkForAutofill();
      }
    };

    // Focus/click events - iOS often requires interaction after autofill
    const handleFocusOrClick = (): void => {
      setTimeout(checkForAutofill, 0);
    };

    // Change/input events - most reliable for iOS and modern browsers
    const handleChangeOrInput = (): void => {
      checkForAutofill();
    };

    input.addEventListener('animationstart', handleAnimationStart as EventListener);
    input.addEventListener('focus', handleFocusOrClick);
    input.addEventListener('click', handleFocusOrClick);
    input.addEventListener('change', handleChangeOrInput);
    input.addEventListener('input', handleChangeOrInput);

    return () => {
      input.removeEventListener('animationstart', handleAnimationStart as EventListener);
      input.removeEventListener('focus', handleFocusOrClick);
      input.removeEventListener('click', handleFocusOrClick);
      input.removeEventListener('change', handleChangeOrInput);
      input.removeEventListener('input', handleChangeOrInput);
    };
  },
  [
    value,
    onChangeAddress,
    onBlur,
    setZipCode,
  ]);

  const handleChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Clear custom validity message when user starts typing
    event.target.setCustomValidity('');
    onChangeAddress(event.target.value);
    debouncedFetchAddressSuggestions(event.target.value);
    setFocusedIndex(-1);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || addressSuggestions.length === 0) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setFocusedIndex(prev => (prev < addressSuggestions.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setFocusedIndex(prev => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        event.preventDefault();

        if (focusedIndex >= 0 && focusedIndex < addressSuggestions.length) {
          handlePickAddressSuggestion(addressSuggestions[focusedIndex]);
          setFocusedIndex(-1);
        }
        break;
      case 'Escape':
        event.preventDefault();
        setShowSuggestions(false);
        setFocusedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleInvalid = (event: React.InvalidEvent<HTMLInputElement>) => {
    if (onInvalidField) {
      event.preventDefault();
      onInvalidField();
    }

    const input = event.target;

    if (input.validity.valueMissing) {
      input.setCustomValidity('Please fill out the address field');
    } else {
      input.setCustomValidity('');
    }
  };

  const renderSuggestion = (suggestion: AutoCompleteAddress) => {
    const wholeAddress = getFormattedAddress(suggestion);
    const [
      commonPart,
      restOfTheString,
    ] = findLongestCommonSubstring(value, wholeAddress);

    return (
      <>
        {commonPart}
        <strong>
          {restOfTheString}
        </strong>
      </>
    );
  };

  return (
    <AddressInputWrapper ref={wrapperRef}>
      <InputWrapper
        backgroundcolor={backgroundcolor}
        error={error}
        fieldName="streetAddress"
        helpText={helpText}
        isRequired={isRequired}
      >
        <input
          // We want autocomplete off for the address to ensure the
          // address validation is used, but 'new-value' is an invalid
          // value and at least in a11y tests this is flagged as an issue.
          // Of note: browsers can still auto-fill (which is good).
          autoComplete="off"
          aria-activedescendant={
            focusedIndex >= 0 ? `address-option-${focusedIndex}` : undefined
          }
          aria-autocomplete="list"
          aria-controls="address-suggestions-listbox"
          aria-describedby={describedBy}
          aria-expanded={showSuggestions && addressSuggestions.length > 0}
          aria-invalid={!!error()}
          aria-label={ariaLabel || 'Address'}
          disabled={isDisabled}
          type="text"
          name="streetAddress"
          placeholder=" "
          role="combobox"
          onChange={handleChangeValue}
          onFocus={onFocus}
          onInvalid={handleInvalid}
          onKeyDown={handleKeyDown}
          required={isRequired}
          defaultValue={value}
          ref={inputRef}
        />
        <span>Address</span>
      </InputWrapper>
      <AddressSuggestions
        id="address-suggestions-listbox"
        role="listbox"
        showSuggestions={showSuggestions}
      >
        {isAutocompletePending &&
          !addressSuggestions.length && <AddressSuggestion>...Loading</AddressSuggestion>
        }
        {addressSuggestions.map((addressSuggestion, index) => (
          <AddressSuggestion
            aria-selected={focusedIndex === index}
            data-testid="address-suggestion"
            id={`address-option-${index}`}
            isFocused={focusedIndex === index}
            key={`${addressSuggestion.streetLine}${addressSuggestion.entries}${addressSuggestion.secondary}`}
            onClick={() => handlePickAddressSuggestion(addressSuggestion)}
            role="option"
          >
            {renderSuggestion(addressSuggestion)}
          </AddressSuggestion>
        ))}
      </AddressSuggestions>
    </AddressInputWrapper>
  );
};

AutoAddressInput.defaultProps = {
  helpText: '',
  isDisabled: false,
  isRequired: false,
  onBlur: () => null,
};
